import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';

import styled from '@emotion/styled';
import { Form as FormikForm, Formik, FormikErrors } from 'formik';

import { Alert, NavLinkButton, Breadcrumb, Button, Panel } from '@shared/components/bootstrap';
import { FormikCheckboxFormGroup } from '@shared/components/fields/formik/formik_checkbox_form_group';
import { FormikInputFormGroup } from '@shared/components/fields/formik/formik_input_form_group';
import { FormikMultiselectFormGroup } from '@shared/components/fields/formik/formik_multiselect_form_group';
import { FormikNumberFormGroup } from '@shared/components/fields/formik/formik_number_input_group';
import { Spacer } from '@shared/components/helpers';

import { PageHeader } from '@admin/components/helpers/page_header';
import { jobAvailabilityListURL, warehouseCapacityListURL } from '@admin/config/routes';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  Status,
  Day,
  useWarehousesSelectorQuery,
  useAvailabilityOperatingScheduleTemplateCreateMutation,
  Availability__OperatingScheduleTemplateCreate__SchedulableType,
  useRegionsSelectorQuery,
  Availability__JobAvailabilityKindEnum,
  Availability__OperatingScheduleTemplateCreate__OperatingDayInput,
} from '@admin/schema';

import { KIND_OPTIONS } from '../job_availability/utils';

type FormInput = {
  effectiveDate: string;
  warehouseIDs: string[];
  regionIDs: string[];
  sunday: boolean;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  jobKinds?: Availability__JobAvailabilityKindEnum[];
  sundayBookedPercentageCloseTrigger?: number;
  mondayBookedPercentageCloseTrigger?: number;
  tuesdayBookedPercentageCloseTrigger?: number;
  wednesdayBookedPercentageCloseTrigger?: number;
  thursdayBookedPercentageCloseTrigger?: number;
  fridayBookedPercentageCloseTrigger?: number;
  saturdayBookedPercentageCloseTrigger?: number;
  sundayBookedPercentageCloseTriggerMinDaysOut?: number;
  mondayBookedPercentageCloseTriggerMinDaysOut?: number;
  tuesdayBookedPercentageCloseTriggerMinDaysOut?: number;
  wednesdayBookedPercentageCloseTriggerMinDaysOut?: number;
  thursdayBookedPercentageCloseTriggerMinDaysOut?: number;
  fridayBookedPercentageCloseTriggerMinDaysOut?: number;
  saturdayBookedPercentageCloseTriggerMinDaysOut?: number;
};

const DEFAULT_FORM_VALUES = {
  effectiveDate: '',
  warehouseIDs: [],
  regionIDs: [],
  sunday: false,
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false,
  jobKinds: [],
};

const WarningMessage = styled.p`
  color: orange;
  margin-top: -14px;
`;

export const WarehouseOperatingScheduleTemplateCreateForm: React.FC = () => {
  useTitle('Warehouse Capacity - New Weekly Schedule');
  return (
    <OperatingScheduleTemplateCreateForm
      schedulableType={Availability__OperatingScheduleTemplateCreate__SchedulableType.Warehouse}
    />
  );
};

export const RegionOperatingScheduleTemplateCreateForm: React.FC = () => {
  useTitle('Job Availability - New Weekly Schedule');
  return (
    <OperatingScheduleTemplateCreateForm
      schedulableType={Availability__OperatingScheduleTemplateCreate__SchedulableType.Region}
    />
  );
};

const CloseTriggerFields: React.FC<{ day: Day }> = ({ day }) => (
  <>
    <p style={{ fontWeight: 'bold' }}>Booked Percentage Close Trigger</p>
    <p>
      If you would like to close the market on this day after a certain percentage of available minutes are booked,
      enter that value below.
    </p>
    <p>
      As an example, if you enter a value of 25%, then the market would be closed once 25% of available minutes are
      booked.
    </p>
    <p>If you do not enter a value, the market will be open until 100% of available minutes are booked.</p>
    <FormikNumberFormGroup
      id={`${day.toLowerCase()}BookedPercentageCloseTrigger`}
      name={`${day.toLowerCase()}BookedPercentageCloseTrigger`}
      min={1}
      max={99}
    />
    <Spacer height="16px" />
    <p style={{ fontWeight: 'bold' }}>Booked Percentage Close Trigger Minimum Days Out</p>
    <p>
      If you would like the close trigger to only apply beyond a certain number of days out, enter that value below.
    </p>
    <p>
      As an example, if you enter a value of 2, the market will be reopened, starting 2 days out, regardless of the
      booked minutes.
    </p>
    <p>If you do not enter a value, the close trigger will always apply.</p>
    <FormikNumberFormGroup
      id={`${day.toLowerCase()}BookedPercentageCloseTriggerMinDaysOut`}
      name={`${day.toLowerCase()}BookedPercentageCloseTriggerMinDaysOut`}
      min={1}
      max={30}
    />
    <Spacer height="24px" />
  </>
);

const OperatingScheduleTemplateCreateForm: React.FC<{
  schedulableType: Availability__OperatingScheduleTemplateCreate__SchedulableType;
}> = ({ schedulableType }) => {
  const [error, setError] = useState<string | undefined>();

  const [save, { loading, data }] = useAvailabilityOperatingScheduleTemplateCreateMutation({ client });
  const { data: warehousesData } = useWarehousesSelectorQuery({ client, variables: { filters: { active: true } } });
  const { data: regionsData } = useRegionsSelectorQuery({ client });

  const warehouses = warehousesData?.warehouses ?? [];
  const regions = regionsData?.regions ?? [];

  const previousPageURL = () => {
    switch (schedulableType) {
      case Availability__OperatingScheduleTemplateCreate__SchedulableType.Warehouse:
        return warehouseCapacityListURL();
      case Availability__OperatingScheduleTemplateCreate__SchedulableType.Region:
        return jobAvailabilityListURL();
    }
  };

  const validateInput = ({ warehouseIDs, regionIDs, jobKinds }: FormInput): FormikErrors<FormInput> => {
    const formErrors: FormikErrors<FormInput> = {};

    if (
      schedulableType === Availability__OperatingScheduleTemplateCreate__SchedulableType.Warehouse &&
      (!warehouseIDs || warehouseIDs.length === 0)
    ) {
      formErrors.warehouseIDs = 'At least one warehouse is required';
    }

    if (schedulableType === Availability__OperatingScheduleTemplateCreate__SchedulableType.Region) {
      if (!regionIDs || regionIDs.length === 0) {
        formErrors.regionIDs = 'At least one region is required';
      }

      if (!jobKinds || jobKinds.length === 0) {
        formErrors.jobKinds = 'At least one job kind is required';
      }
    }

    return formErrors;
  };

  const onSubmit = async ({
    effectiveDate,
    warehouseIDs,
    regionIDs,
    jobKinds,
    sunday,
    monday,
    tuesday,
    wednesday,
    thursday,
    friday,
    saturday,
    sundayBookedPercentageCloseTrigger,
    mondayBookedPercentageCloseTrigger,
    tuesdayBookedPercentageCloseTrigger,
    wednesdayBookedPercentageCloseTrigger,
    thursdayBookedPercentageCloseTrigger,
    fridayBookedPercentageCloseTrigger,
    saturdayBookedPercentageCloseTrigger,
    sundayBookedPercentageCloseTriggerMinDaysOut,
    mondayBookedPercentageCloseTriggerMinDaysOut,
    tuesdayBookedPercentageCloseTriggerMinDaysOut,
    wednesdayBookedPercentageCloseTriggerMinDaysOut,
    thursdayBookedPercentageCloseTriggerMinDaysOut,
    fridayBookedPercentageCloseTriggerMinDaysOut,
    saturdayBookedPercentageCloseTriggerMinDaysOut,
  }: FormInput) => {
    const operatingDays: Availability__OperatingScheduleTemplateCreate__OperatingDayInput[] = [];
    if (sunday) {
      operatingDays.push({
        day: Day.Sunday,
        bookedPercentageCloseTrigger: sundayBookedPercentageCloseTrigger,
        bookedPercentageCloseTriggerMinDaysOut: sundayBookedPercentageCloseTrigger
          ? sundayBookedPercentageCloseTriggerMinDaysOut
          : undefined,
      });
    }
    if (monday) {
      operatingDays.push({
        day: Day.Monday,
        bookedPercentageCloseTrigger: mondayBookedPercentageCloseTrigger,
        bookedPercentageCloseTriggerMinDaysOut: mondayBookedPercentageCloseTrigger
          ? mondayBookedPercentageCloseTriggerMinDaysOut
          : undefined,
      });
    }
    if (tuesday) {
      operatingDays.push({
        day: Day.Tuesday,
        bookedPercentageCloseTrigger: tuesdayBookedPercentageCloseTrigger,
        bookedPercentageCloseTriggerMinDaysOut: tuesdayBookedPercentageCloseTrigger
          ? tuesdayBookedPercentageCloseTriggerMinDaysOut
          : undefined,
      });
    }
    if (wednesday) {
      operatingDays.push({
        day: Day.Wednesday,
        bookedPercentageCloseTrigger: wednesdayBookedPercentageCloseTrigger,
        bookedPercentageCloseTriggerMinDaysOut: wednesdayBookedPercentageCloseTrigger
          ? wednesdayBookedPercentageCloseTriggerMinDaysOut
          : undefined,
      });
    }
    if (thursday) {
      operatingDays.push({
        day: Day.Thursday,
        bookedPercentageCloseTrigger: thursdayBookedPercentageCloseTrigger,
        bookedPercentageCloseTriggerMinDaysOut: thursdayBookedPercentageCloseTrigger
          ? thursdayBookedPercentageCloseTriggerMinDaysOut
          : undefined,
      });
    }
    if (friday) {
      operatingDays.push({
        day: Day.Friday,
        bookedPercentageCloseTrigger: fridayBookedPercentageCloseTrigger,
        bookedPercentageCloseTriggerMinDaysOut: fridayBookedPercentageCloseTrigger
          ? fridayBookedPercentageCloseTriggerMinDaysOut
          : undefined,
      });
    }
    if (saturday) {
      operatingDays.push({
        day: Day.Saturday,
        bookedPercentageCloseTrigger: saturdayBookedPercentageCloseTrigger,
        bookedPercentageCloseTriggerMinDaysOut: saturdayBookedPercentageCloseTrigger
          ? saturdayBookedPercentageCloseTriggerMinDaysOut
          : undefined,
      });
    }

    const schedulableIDs = () => {
      switch (schedulableType) {
        case Availability__OperatingScheduleTemplateCreate__SchedulableType.Warehouse:
          return warehouseIDs;
        case Availability__OperatingScheduleTemplateCreate__SchedulableType.Region:
          return regionIDs;
      }
    };

    setError(undefined);

    const results = await save({
      variables: {
        input: {
          effectiveDate,
          schedulableIDs: schedulableIDs(),
          schedulableType: schedulableType,
          operatingDays: operatingDays,
          jobKinds:
            schedulableType === Availability__OperatingScheduleTemplateCreate__SchedulableType.Region
              ? jobKinds
              : undefined,
        },
      },
    });

    if (results.data?.result.status === Status.Ok) {
      window.location.href = previousPageURL();
    } else {
      setError(results.data?.result.error ?? 'An unexpected error occurred. Please try again.');
    }
  };

  const allowSettingCloseTriggers =
    schedulableType === Availability__OperatingScheduleTemplateCreate__SchedulableType.Region;

  return (
    <>
      <PageHeader>
        <Breadcrumb>
          {schedulableType === Availability__OperatingScheduleTemplateCreate__SchedulableType.Warehouse && (
            <Breadcrumb.Item>
              <NavLink to={warehouseCapacityListURL()}>Warehouse Capacity</NavLink>
            </Breadcrumb.Item>
          )}
          {schedulableType === Availability__OperatingScheduleTemplateCreate__SchedulableType.Region && (
            <Breadcrumb.Item>
              <NavLink to={jobAvailabilityListURL()}>Job Availability</NavLink>
            </Breadcrumb.Item>
          )}
          <Breadcrumb.Item active>New Weekly Schedule</Breadcrumb.Item>
        </Breadcrumb>
      </PageHeader>
      <Formik<FormInput>
        initialValues={DEFAULT_FORM_VALUES}
        onSubmit={onSubmit}
        validate={validateInput}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ values }) => (
          <FormikForm>
            <Panel>
              <Panel.Body>
                {data?.result?.status === Status.Ok && (
                  <Alert style="success">{'Successfully updated warehouse operating schedules.'}</Alert>
                )}
                <FormikInputFormGroup id="effectiveDate" type="date" name="effectiveDate" label="Effective Date:" />
                {schedulableType === Availability__OperatingScheduleTemplateCreate__SchedulableType.Warehouse && (
                  <>
                    <WarningMessage>
                      The weekly operating schedule for the selected warehouses will be updated from the effective date
                      forward. Any existing weekly schedules in the future will be overwritten.
                    </WarningMessage>
                    <FormikMultiselectFormGroup
                      id="warehouseIDs"
                      name="warehouseIDs"
                      label="Warehouses:"
                      entries={warehouses}
                    />
                  </>
                )}
                {schedulableType === Availability__OperatingScheduleTemplateCreate__SchedulableType.Region && (
                  <>
                    <WarningMessage>
                      The weekly operating schedule for the selected regions will be updated from the effective date
                      forward. Any existing weekly schedules in the future will be overwritten.
                    </WarningMessage>
                    <FormikMultiselectFormGroup id="regionIDs" name="regionIDs" label="Regions:" entries={regions} />
                    <FormikMultiselectFormGroup
                      id="jobKinds"
                      name="jobKinds"
                      label="Job Kinds:"
                      entries={KIND_OPTIONS}
                    />
                  </>
                )}
                <FormikCheckboxFormGroup label="Open Sunday" id="sunday" name="sunday" />
                {allowSettingCloseTriggers && values.sunday && <CloseTriggerFields day={Day.Sunday} />}

                <FormikCheckboxFormGroup label="Open Monday" id="monday" name="monday" />
                {allowSettingCloseTriggers && values.monday && <CloseTriggerFields day={Day.Monday} />}

                <FormikCheckboxFormGroup label="Open Tuesday" id="tuesday" name="tuesday" />
                {allowSettingCloseTriggers && values.tuesday && <CloseTriggerFields day={Day.Tuesday} />}

                <FormikCheckboxFormGroup label="Open Wednesday" id="wednesday" name="wednesday" />
                {allowSettingCloseTriggers && values.wednesday && <CloseTriggerFields day={Day.Wednesday} />}

                <FormikCheckboxFormGroup label="Open Thursday" id="thursday" name="thursday" />
                {allowSettingCloseTriggers && values.thursday && <CloseTriggerFields day={Day.Thursday} />}

                <FormikCheckboxFormGroup label="Open Friday" id="friday" name="friday" />
                {allowSettingCloseTriggers && values.friday && <CloseTriggerFields day={Day.Friday} />}

                <FormikCheckboxFormGroup label="Open Saturday" id="saturday" name="saturday" />
                {allowSettingCloseTriggers && values.saturday && <CloseTriggerFields day={Day.Saturday} />}

                {error && <Alert style="danger">{error}</Alert>}
              </Panel.Body>
              <Panel.Footer align="right">
                <NavLinkButton disabled={loading} kind="default" to={previousPageURL()}>
                  Cancel
                </NavLinkButton>
                <Button loading={loading} kind="primary" type="submit">
                  Save
                </Button>
              </Panel.Footer>
            </Panel>
          </FormikForm>
        )}
      </Formik>
    </>
  );
};
